<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import { ref } from 'vue'

const currentStep = ref(0)
const isReverse = ref(false)

const listValue = ref([
  { text: '', amount: null, warranty: 'Garantiefall', description: '', detail: '' }
])
const listIncomplete = ref(false)

function validateList(handlers: any, node: any) {
  const isValid = listValue.value.every(
    (item) =>
      item.text &&
      item.amount !== null &&
      item.amount !== 0 &&
      item.amount !== '0' &&
      item.warranty &&
      item.description &&
      item.detail
  )
  if (isValid) {
    handlers.incrementStep(1, node.context)()
    moveStep(1)
    listIncomplete.value = false
  } else {
    listIncomplete.value = true
  }
}

const contactData = ref({
  firstName: '',
  lastName: '',
  company: '',
  street: '',
  city: '',
  postcode: '',
  phone: '',
  email: '',
  account: false
})

const submitted = ref(false)
const contactErrors = ref<Array<string>>([])

function isRequired(value: any, message: any) {
  if (value.trim() === '') {
    return { valid: false, message }
  }
  return { valid: true }
}

function isEmail(value: any, message: any) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return { valid: false, message }
  }
  return { valid: true }
}

function isPhoneNumber(value: string, message: string) {
  const phoneRegex = /^(\+?\d{1,3}[-\s]?)?(\(?\d+\)?[-\s]?)*\d+$/
  if (!phoneRegex.test(value)) {
    return { valid: false, message }
  }
  return { valid: true }
}

function isNumber(value: any, message: any) {
  if (!/^\d+$/.test(value)) {
    return { valid: false, message }
  }
  return { valid: true }
}

const validationChecks = {
  firstName: [(value: string) => isRequired(value, 'Vorname ist ein Pflichtfeld')],
  lastName: [(value: string) => isRequired(value, 'Nachname ist ein Pflichtfeld')],
  street: [(value: string) => isRequired(value, 'Straße & Haus Nr. ist ein Pflichtfeld')],
  city: [(value: string) => isRequired(value, 'Stadt ist ein Pflichtfeld')],
  postcode: [
    (value: string) => isRequired(value, 'PLZ ist ein Pflichtfeld'),
    (value: string) => isNumber(value, 'PLZ ist nicht gültig')
  ],
  phone: [
    (value: string) => isRequired(value, 'Telefonnummer ist ein Pflichtfeld'),
    (value: string) => isPhoneNumber(value, 'Telefonnummer ist nicht gültig')
  ],
  email: [
    (value: string) => isRequired(value, 'E-Mail ist ein Pflichtfeld'),
    (value: string) => isEmail(value, 'E-Mail ist nicht gültig')
  ]
}

function validateContactData() {
  const errors = []

  // Iterate over validationChecks in the specified order (firstName, lastName, ...).
  for (const [field, checks] of Object.entries(validationChecks)) {
    const value = contactData.value[field as keyof typeof contactData.value]

    for (const check of checks) {
      if (typeof value === 'string') {
        const result = check(value)
        if (!result.valid) {
          errors.push(result.message)
        }
      }
    }
  }

  return errors
}

const submitData = async () => {
  const errors = validateContactData()
  if (errors.length > 0) {
    contactErrors.value = errors
    return
  }

  const formData = {
    list: listValue.value,
    firstName: contactData.value.firstName,
    lastName: contactData.value.lastName,
    email: contactData.value.email,
    company: contactData.value.company,
    street: contactData.value.street,
    city: contactData.value.city,
    postcode: contactData.value.postcode,
    phone: contactData.value.phone,
    account: contactData.value.account ? true : false
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_DIRECTUS_URL}/items/form_data?access_token=${
        import.meta.env.VITE_DIRECTUS_TOKEN
      }`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      }
    )
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  } catch (error) {
    console.error(error)
  }

  submitted.value = true
  moveStep(1)
}

const moveStep = (direction: number) => {
  if (direction < 0) {
    isReverse.value = true
  } else {
    isReverse.value = false
  }
  currentStep.value += direction
}

const formatIndex = (index: number) => {
  index = index + 1
  if (index < 10) {
    return `0${index}`
  }
  return index
}

const instance = getCurrentInstance()
const print = () => {
  instance?.appContext.config.globalProperties.$htmlToPaper('print')
}
</script>

<template>
  <div class="hope-form">
    <div class="header">
      <h1 v-if="currentStep === 0">Service Formular</h1>
      <div v-else class="progress-header">
        <span
          class="status-number"
          :class="{
            highlighted: currentStep >= 1,
            current: currentStep === 1,
            completed: currentStep > 1
          }"
          >01</span
        >
        <div class="separator" :class="{ animated: currentStep >= 2, reverse: isReverse }"></div>
        <span
          class="status-number"
          :class="{
            highlighted: currentStep >= 2,
            current: currentStep === 2,
            completed: currentStep > 2
          }"
          >02</span
        >
        <div class="separator" :class="{ animated: currentStep >= 3, reverse: isReverse }"></div>
        <span
          class="status-number"
          :class="{
            highlighted: currentStep >= 3,
            current: currentStep === 3,
            completed: currentStep > 3
          }"
          >03</span
        >
      </div>
    </div>
    <FormKit
      type="form"
      name="hope-form"
      :actions="false"
      id="form-container"
      @submit="submitData"
      v-if="!submitted"
    >
      <FormKit
        type="multi-step"
        tab-style="progress"
        :hide-progress-labels="true"
        :allow-incomplete="false"
        name="formData"
        id="form"
      >
        <FormKit type="step" name="welcome">
          <div class="heading">
            <h2>Willkommen bei Deinem Hope Service Partner</h2>
            <p>
              Fülle einfach das Online Formular aus, drucke es am Ende dieses Fragebogens aus und
              lege es mit in Dein Paket, das Du an uns versendest.
            </p>
          </div>
          <template #stepNext="{ handlers, node }">
            <FormKit
              outer-class="next-button"
              input-class="next-button-input"
              type="button"
              @click="handlers.incrementStep(1, node.context)(), moveStep(1)"
              label="Weiter"
              data-next="true"
            />
          </template>
        </FormKit>

        <FormKit type="step">
          <div class="heading">
            <h2>Deine Teile auswählen</h2>
            <p>
              Auf der nächsten Seite wirst Du nach Deinen Hope Teilen gefragt: Bitte gebe zu allen
              Teilen auch eine
              <strong>vollständige Fehlerbeschreibung</strong> mit an und, ob es sich um einen
              <strong>Garantiefall</strong> oder um einen <strong>Servicefall</strong> handelt.
            </p>
          </div>
          <template #stepPrevious="{ handlers, node }">
            <FormKit
              outer-class="prev-button"
              input-class="prev-button-input"
              type="button"
              @click="handlers.incrementStep(-1, node.context)(), moveStep(-1)"
              label="Zurück"
            />
          </template>
          <template #stepNext="{ handlers, node }">
            <FormKit
              outer-class="next-button"
              input-class="next-button-input"
              type="button"
              @click="handlers.incrementStep(1, node.context)(), moveStep(1)"
              label="Weiter"
              data-next="true"
            />
          </template>
        </FormKit>

        <FormKit type="step" name="list">
          <div class="heading">
            <h2>Art und Anzahl</h2>
            <p>
              Gib die Typenbezeichnung an und wie viele der Teile Du jeweils zu uns sendest. Mit
              einem Klick auf das "+" kannst Du weitere Parts hinzufügen.
            </p>
          </div>
          <FormKit type="list" name="listValues" class="list-items">
            <div v-for="(item, index) in listValue" :key="index" class="list-item">
              <div class="trash-icon-wrapper">
                <p class="list-input-heading">Teil {{ formatIndex(index) }}</p>
                <img
                  :style="{
                    opacity: index === 0 ? '0' : '1',
                    pointerEvents: index === 0 ? 'none' : 'auto'
                  }"
                  src="../assets/trash.svg"
                  v-if="index !== 0 || true"
                  @click="index !== 0 && listValue.splice(index, 1)"
                  class="trash-icon"
                />
              </div>
              <div class="list-input">
                <FormKit
                  :index="index"
                  outer-class="list-input-text"
                  type="select"
                  name="text"
                  v-model="item.text"
                >
                  <option value="" disabled selected>Wähle Dein Teil aus</option>
                  <optgroup label="Bremsen">
                    <option value="Tech">Tech</option>
                    <option value="Tech Evo">Tech Evo</option>
                    <option value="Tech 3">Tech 3</option>
                    <option value="Tech 4">Tech 4</option>
                    <option value="Race">Race</option>
                    <option value="Race Evo">Race Evo</option>
                    <option value="XCR">XCR</option>
                  </optgroup>
                  <optgroup label="Naben">
                    <option value="RS4 Vorne">RS4 Vorne</option>
                    <option value="Pro4 Vorne">Pro4 Vorne</option>
                    <option value="Pro5 Vorne">Pro5 Vorne</option>
                    <option disabled></option>
                    <option value="RS4 Hinten">RS4 Hinten</option>
                    <option value="Pro4 Hinten">Pro4 Hinten</option>
                    <option value="Pro5 Hinten">Pro5 Hinten</option>
                  </optgroup>
                  <optgroup label="Laufräder">
                    <option value="Fortus 23">Fortus 23</option>
                    <option value="Fortus 26">Fortus 26</option>
                    <option value="Fortus 30">Fortus 30</option>
                    <option value="Fortus 35">Fortus 35</option>
                    <option value="20Five">20Five</option>
                  </optgroup>
                  <optgroup label="Pedale">
                    <option value="F12">F12</option>
                    <option value="F20">F20</option>
                    <option value="F22">F22</option>
                    <option value="Union TC">Union TC</option>
                    <option value="Union RC">Union RC</option>
                    <option value="Union GC">Union GC</option>
                  </optgroup>
                  <optgroup label="Sonstige">
                    <option value="Sonstige">Dein Produkt ist nicht dabei</option>
                  </optgroup>
                </FormKit>

                <FormKit
                  :index="index"
                  outer-class="list-input-number"
                  type="number"
                  name="amount"
                  v-model="item.amount"
                  min="1"
                  max="100"
                  placeholder="Anzahl"
                />
                <FormKit
                  :index="index"
                  outer-class="list-input-select"
                  type="select"
                  name="warranty"
                  v-model="item.warranty"
                  placeholder="Garantiefall"
                  :options="['Garantiefall', 'Service']"
                />

                <!-- Dependent select -->
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="select"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  v-if="
                    item.text === 'Tech' ||
                    item.text === 'Tech Evo' ||
                    item.text === 'Tech 3' ||
                    item.text === 'Tech 4'
                  "
                >
                  <option value="" disabled selected>Wähle das genaue Modell aus</option>
                  <option value="X2">X2</option>
                  <option value="E4">E4</option>
                  <option value="V2">V2</option>
                  <option value="V4">V4</option>
                </FormKit>
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="select"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  v-if="item.text === 'Race' || item.text === 'Race Evo' || item.text === 'XCR'"
                >
                  <option value="" disabled selected>Wähle das genaue Modell aus</option>
                  <option value="X2">X2</option>
                </FormKit>
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="select"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  v-if="
                    item.text === 'RS4 Vorne' ||
                    item.text === 'Pro4 Vorne' ||
                    item.text === 'Pro5 Vorne' ||
                    item.text === 'RS4 Hinten' ||
                    item.text === 'Pro4 Hinten' ||
                    item.text === 'Pro5 Hinten'
                  "
                >
                  <option value="" disabled selected>Wähle das genaue Modell aus</option>
                  <option value="XD Freilauf">XD Freilauf</option>
                  <option value="HG Freilauf">HG Freilauf</option>
                  <option value="Microspline Freilauf">Microspline Freilauf</option>
                  <option value="Campagnolo Freilauf">Campagnolo Freilauf</option>
                </FormKit>
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="select"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  v-if="
                    item.text === 'Fortus 23' ||
                    item.text === 'Fortus 26' ||
                    item.text === 'Fortus 30' ||
                    item.text === 'Fortus 35' ||
                    item.text === '20Five'
                  "
                >
                  <option value="" disabled selected>Wähle das genaue Modell aus</option>
                  <option value="Vorderrad">Vorderrad</option>
                  <option value="Hinterrad">Hinterrad</option>
                </FormKit>
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="select"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  v-if="
                    item.text === 'F12' ||
                    item.text === 'F20' ||
                    item.text === 'F22' ||
                    item.text === 'Union TC' ||
                    item.text === 'Union RC' ||
                    item.text === 'Union GC'
                  "
                >
                  <option value="info" selected>Pedale immer als Set einsenden</option>
                </FormKit>
                <FormKit
                  :index="index"
                  outer-class="list-input-detail"
                  type="text"
                  name="text"
                  v-model="item.detail"
                  validation="required:trim"
                  :validation-messages="{
                    required: 'Bitte gib ein Teil an.'
                  }"
                  validation-visibility="submit"
                  placeholder="Teilbezeichnung"
                  v-if="item.text === 'Sonstige'"
                >
                </FormKit>
              </div>
              <div class="textarea-wrapper">
                <FormKit
                  :index="index"
                  outer-class="list-input-textarea"
                  type="textarea"
                  v-model="item.description"
                  placeholder="Fehlerbeschreibung / Servicewunsch"
                />
              </div>
            </div>
            <div class="button-wrapper">
              <button
                id="add-button"
                type="button"
                @click="
                  listValue.push({
                    text: '',
                    amount: null,
                    warranty: 'Garantiefall',
                    description: '',
                    detail: ''
                  })
                "
              >
                +
              </button>
            </div>
            <p class="error-message" v-if="listIncomplete">Bitte fülle alle Felder aus.</p>
          </FormKit>
          <template #stepPrevious="{ handlers, node }">
            <FormKit
              outer-class="prev-button"
              input-class="prev-button-input"
              type="button"
              @click="handlers.incrementStep(-1, node.context)(), moveStep(-1)"
              label="Zurück"
            />
          </template>
          <template #stepNext="{ handlers, node }">
            <FormKit
              outer-class="next-button"
              input-class="next-button-input"
              type="button"
              @click="() => validateList(handlers, node)"
              label="Weiter"
              data-next="true"
            />
          </template>
        </FormKit>

        <FormKit type="step" name="contactData">
          <div class="heading">
            <h2>Deine Kontaktdaten</h2>
            <p>
              Trage hier noch Deine persönlichen Daten ein, damit wir Dich zuordnen und bei
              Rückfragen kontaktieren können
            </p>
          </div>
          <div class="contact-input-wrapper">
            <input class="contact-input" placeholder="Vorname" v-model="contactData.firstName" />
            <input class="contact-input" placeholder="Nachname" v-model="contactData.lastName" />
            <input class="contact-input" placeholder="Firma" v-model="contactData.company" />
            <input
              class="contact-input"
              placeholder="Straße & Haus Nr."
              v-model="contactData.street"
            />
            <input class="contact-input" placeholder="Stadt" v-model="contactData.city" />
            <input class="contact-input" placeholder="PLZ" v-model="contactData.postcode" />
            <input class="contact-input" placeholder="Telefon" v-model="contactData.phone" />
            <input class="contact-input" placeholder="E-Mail" v-model="contactData.email" />
            <label id="contact-checkbox">
              <input type="checkbox" name="checkbox" v-model="contactData.account" />
              <span
                >Registrieren und
                <a href="/downloads" target="_blank">Datenschutzerklärung</a>
                akzeptieren.</span
              >
            </label>
          </div>
          <div v-if="contactErrors.length > 0" class="error-message">
            <ul>
              <li v-for="error in contactErrors" :key="error">{{ error }}</li>
            </ul>
          </div>

          <template #stepPrevious="{ handlers, node }">
            <FormKit
              outer-class="prev-button"
              input-class="prev-button-input"
              type="button"
              @click="handlers.incrementStep(-1, node.context)(), moveStep(-1)"
              label="Zurück"
            />
          </template>
          <template #stepNext>
            <FormKit
              outer-class="next-button"
              input-class="next-button-input"
              type="submit"
              label="Absenden"
            />
          </template>
        </FormKit>

        <div class="footer"></div>
      </FormKit>
    </FormKit>
    <div v-if="submitted" class="finish">
      <div class="heading">
        <h2>Fast geschafft! Bald geht's wieder auf's Rad</h2>
        <p>
          <b
            >⚠️ Denke bitte daran dieses Formular auszudrucken und Deinem Paket beizulegen! <br />
            Für Garantiefälle lege bitte ebenfalls eine Rechnungskopie bei.</b
          ><br />
          Schicke das ausgefüllte Formular und Deine Teile bitte an:
          <br />
          <br />
          <b>MTB-Store</b><br />
          <b>Hope European Service Centre</b><br />
          Service Partner<br />
          Langwahn 100<br />
          52249 Eschweiler<br />

          <br />
          Keine Sorge, wir schicken Dir alles auch nochmal gesondert per E-Mail zu.
        </p>
      </div>
      <div class="finish-button-wrapper">
        <button class="btn form-button" @click="print">
          <img src="../assets/printer.svg" />
          <p>Formular</p>
        </button>
      </div>
      <div
        style="position: absolute; z-index: -10; top: 0; left: 0; bottom: 0; right: 0; opacity: 0"
      >
        <div id="print">
          <div style="text-align: center">
            <img
              src="https://assets-global.website-files.com/64a29f6093ad13ce30f209f4/64a44c1133b491b9984a690b_logo_hope_white-green.svg"
              alt="hope-logo"
              style="width: 200px"
            />
          </div>
          <br />
          <br />
          <h1>Hope Service Formular</h1>
          <h2>Absender</h2>
          <table>
            <tr>
              <td>{{ contactData.firstName }} {{ contactData.lastName }}</td>
            </tr>
            <tr>
              <td>{{ contactData.company }}</td>
            </tr>
            <tr>
              <td>{{ contactData.street }}</td>
            </tr>
            <tr>
              <td>{{ contactData.postcode }} {{ contactData.city }}</td>
            </tr>
            <tr>
              <td>{{ contactData.phone }}</td>
            </tr>
            <tr>
              <td>{{ contactData.email }}</td>
            </tr>
          </table>
          <br />
          <br />
          <h2>Empfänger</h2>
          <table>
            <tr>
              <td>MTB-Store</td>
            </tr>
            <tr>
              <td>Hope European Service Centre</td>
            </tr>
            <tr>
              <td>Service Partner</td>
            </tr>
            <tr>
              <td>Langwahn 100</td>
            </tr>
            <tr>
              <td>52249 Eschweiler</td>
            </tr>
          </table>
          <br />
          <br />
          <h2>Teile</h2>
          <!-- table of listValue -->
          <table style="width: 100%">
            <thead style="text-align: left">
              <tr>
                <th>Teil</th>
                <th>Anzahl</th>
                <th>Garantiefall</th>
                <th>Teilbezeichnung</th>
                <th>Fehlerbeschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in listValue" :key="index">
                <td>{{ item.text }}</td>
                <td>{{ item.amount }}</td>
                <td>{{ item.warranty }}</td>
                <td>{{ item.detail }}</td>
                <td>{{ item.description }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.hope-form {
  .header {
    font-family: 'erbaum', sans-serif;
    background-color: #181818;
    color: #f7f7f7;
    text-align: center;
    padding-top: 120px;
    padding-bottom: 100px;
    height: auto;
    text-transform: uppercase;
  }

  .status-number {
    color: #494949;
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0;
    font-size: 2.5rem;
    font-weight: 500;
    line-height: 120%;
    transition: color 1s;
  }

  .status-number.current {
    color: white;
    transition-delay: 1s;
  }

  .status-number.completed {
    color: #43c035;
  }

  .progress-header {
    display: flex;
    column-gap: 8px;
    align-items: center;
    justify-content: center;
  }

  .highlighted {
    color: #43c035;
  }

  .separator {
    position: relative;
    width: 130px;
    height: 2px;
    background-color: #494949;
    overflow: hidden;
    transition: background-color 1s;
  }

  .separator::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background-color: white;
    transition:
      left 1s,
      right 1s;
  }

  .separator.animated::before {
    left: 0;
  }

  .separator.reverse.animated::before {
    right: 0;
    left: auto;
  }

  .separator.reverse.active::before {
    right: 0;
  }

  .animated {
    background-color: white;
  }

  #form {
    width: 100% !important;
    font-family: 'erbaum', sans-serif;
  }

  #form-container {
    background-color: transparent;
    color: white;
  }

  .formkit-step {
    transition: all 0.4s !important;
  }

  .formkit-tabs {
    display: none !important;
  }

  .formkit-wrapper {
    display: flex !important;
    justify-content: center !important;
  }

  .formkit-outer[data-type='multi-step'] > .formkit-wrapper {
    max-width: none !important;
  }

  .heading {
    text-align: center;
    margin-bottom: 50px;
  }

  .heading h2 {
    font-size: 1.3125rem;
    font-weight: 400;
    margin-bottom: 12px;
    line-height: 120%;
  }

  .heading p {
    font-size: 1rem;
    font-weight: 200;
    line-height: 150%;
  }

  .formkit-steps {
    position: relative;
    border: none !important;
    box-shadow: none !important;
    width: 900px;
    height: 600px;
    padding: 100px 0px !important;
  }

  .formkit-inner {
    width: 100% !important;
  }

  .formkit-step-actions {
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr !important;

    margin-top: 50px !important;
  }

  .formkit-step-actions div div button {
    font-family: 'erbaum', sans-serif;
    font-size: 0.875rem;
    font-weight: 300;
    padding: 12px 16px !important;
    border-radius: 0 !important;
    transition: background-color 0.4s !important;
  }

  .formkit-step-actions div div button:hover {
    background-color: white !important;
  }

  .prev-button {
    margin-right: auto !important;
  }

  .prev-button-input {
    background-color: transparent !important;
    color: #494949 !important;
    border: 1px solid #494949 !important;
  }

  .next-button {
    grid-column: 2;
  }

  .next-button-input {
    background-color: #43c035 !important;
    color: black !important;
  }

  /* Art und Anzahl Step */
  .list-input {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    justify-content: center;
    align-items: center;
    width: 100%;
    font-size: 14px;
  }

  .list-input * {
    color: black !important;
    background-color: white;
    height: 38px;
    border-radius: 0 !important;
    font-family: 'erbaum', sans-serif !important;
  }

  .list-input-heading {
    font-size: 0.875rem;
    font-weight: 300;
    display: inline-block;
  }

  .list-input-text {
    flex: 0 0 calc(60% - 8px);
  }

  .list-input-detail {
    flex: 0 0 calc(100% - 8px);
  }

  .textarea-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .textarea-wrapper * {
    color: black !important;
    background-color: white;
    border-radius: 0 !important;
    font-family: 'erbaum', sans-serif !important;
    font-size: 0.875rem;
    line-height: 1.42857;
    min-height: 0 !important;
  }

  .list-input-textarea {
    width: 100%;
    line-height: 1.42857;
    margin: 0 4px 0 !important;
  }

  .list-input-number {
    flex: 0 0 calc(15% - 8px);
  }

  .list-input-select {
    flex: 0 0 calc(25% - 8px);
  }

  .trash-icon-wrapper {
    display: flex;
    align-items: center;
  }

  .trash-icon {
    width: 12px;
    height: auto;
    margin-left: 16px;
    cursor: pointer;
    background: transparent;
    transition: transform 0.2s;
    /* webflow fix */
    margin-bottom: 10px;
  }

  .trash-icon:hover {
    transform: scale(1.1);
  }

  .button-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  #add-button {
    all: unset;
    margin-top: 0.5em;
    height: 38px;
    border-radius: 0 !important;
    font-size: 3em;
    font-weight: 200;
    color: #494949;
    cursor: pointer;
    transition: color 0.4s;
  }

  #add-button:hover {
    color: white;
  }

  .list-enter-active,
  .list-leave-active {
    transition: all 1s ease;
  }

  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }

  /* Kontaktdaten step */
  .contact-input-wrapper {
    display: flex;
    flex-wrap: wrap;
    box-sizing: border-box;
    font-size: 14px;
  }

  .contact-input {
    all: unset;
    height: 38px;
    padding-left: 10px;
    width: 30%;
    flex-grow: 1;
    box-sizing: border-box;
    margin: 0 10px 10px;
    justify-content: space-between;
    background: transparent;
    border: none;
    border-bottom: 2px solid #494949;
    color: white;
    padding: 0;
    margin-bottom: 2em;
    transition: border-bottom 0.4s;
  }

  .contact-input:focus {
    border-bottom: 2px solid #43c035 !important;
  }

  .contact-input:hover {
    border-bottom: 2px solid white;
  }

  #contact-checkbox {
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    padding-left: 5px;
    font-weight: 300;
    font-size: 14px;
  }

  #contact-checkbox input {
    margin-right: 8px;
    display: block;
    outline: none;
  }

  #contact-checkbox span {
    display: block;
  }

  .list-item {
    margin-top: 16px;
  }

  /* finish */
  .finish {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white;
    font-family: 'erbaum', sans-serif;
    padding: 100px 0;
  }

  h2.emoji {
    margin-top: 0;
    margin-bottom: 12px;
    font-size: 3vw;
    font-weight: 400;
    line-height: 120%;
    position: relative;
  }

  .finish-button-wrapper {
    box-sizing: border-box;
    grid-column-gap: 12px;
    display: flex;
    text-align: center;
    font-size: 14px;
    line-height: 20px;
  }

  .btn {
    background-color: rgba(0, 0, 0, 0);
    border: 1px solid #fff;
    padding: 12px 16px;
    font-weight: 300;
    color: #fff;
    line-height: inherit;
    cursor: pointer;
    border-radius: 0;
    display: inline-block;
    text-align: center;
    box-sizing: border-box;
    transition:
      border-color 0.4s,
      background-color 0.4s,
      color 0.4s;
  }

  .label-button {
    font-size: 0.875rem;
    text-decoration: none;
  }

  .label-button:hover {
    color: #0e0e0e;
    background-color: #fff;
    font-weight: 400;
  }

  .form-button {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column-gap: 8px;
    grid-row-gap: 8px;
    background-color: white;
    color: black;
    text-decoration: none;
  }

  .form-button:hover {
    border-color: #f7f7f7;
    background-color: #d6d6d6;
    font-weight: 400;
  }

  .form-button img {
    width: 24px;
    height: 24px;
    max-width: 100%;
  }

  .form-button p {
    margin: 0;
  }

  /* errors */
  .error-message {
    color: red;
    text-align: center;
    margin-top: 2em;
  }

  .error-message ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .formkit-messages {
    background-color: transparent;
  }

  .formkit-message {
    color: red !important;
    background-color: transparent;
    width: 150%;
  }

  @media (max-width: 991px) {
    .footer {
      position: fixed;
      z-index: 9;
      width: 100%;
      height: 120px;
      backdrop-filter: blur(5px);
      background-color: rgba(14, 14, 14, 0.5);
      padding-bottom: 0;
      padding-right: 0;
      display: block;
      position: fixed;
      top: auto;
      bottom: 0%;
      left: 0%;
      right: 0%;
    }

    .formkit-steps {
      margin: 0 20px 0;
    }

    .header {
      height: auto;
    }

    .formkit-step-inner {
      padding-bottom: 25em !important;
    }

    .next-button {
      z-index: 10;
      position: fixed;
      top: auto;
      bottom: 25px;
      left: auto;
      right: 40px;
      transform: none;
    }

    .prev-button {
      z-index: 10;
      position: fixed;
      bottom: 25px;
      left: 40px;
    }

    .contact-input-wrapper {
      flex-direction: column;
    }

    .contact-input-wrapper > *:nth-child(3n) {
      margin-bottom: 2em;
    }

    .contact-input {
      width: 100%;
      margin: 0 0 0.75em 0;
    }

    #contact-checkbox {
      margin-top: 2em;
    }
  }

  @media (max-width: 750px) {
    .list-input-text {
      flex: 0 0 calc(80% - 8px);
    }

    .list-input-number {
      flex: 0 0 calc(20% - 8px);
    }

    .list-input-select {
      flex: 0 0 calc(100% - 8px);
    }
  }

  @media (max-width: 500px) {
    .list-input-text {
      flex: 0 0 calc(70% - 8px);
    }

    .list-input-number {
      flex: 0 0 calc(30% - 8px);
    }

    .header {
      padding: 100px 20px 40px;
    }

    .status-number {
      font-size: 2rem;
    }

    .separator {
      width: 60px;
    }

    .contact-input {
      margin: 0 0 0.75em 0;
    }
  }

  @media screen and (-webkit-min-device-pixel-ratio: 0) and (max-width: 991px) {
    _::-webkit-full-page-media,
    _:future,
    :root,
    .button-wrapper {
      padding-bottom: 10em;
    }
  }
}
</style>
